import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star } from 'lucide-react';

import type { SiteContent } from '../../content/types';
import { formatDate } from '../../lib/utils';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Container } from '../layout/container';
import { SectionHeading } from './section-heading';

interface ReviewSliderProps {
  content: SiteContent;
}

function Stars({ rating }: { rating: number }): JSX.Element {
  return (
    <div className="inline-flex items-center gap-1" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star key={index} className={`h-4 w-4 ${index < rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      ))}
    </div>
  );
}

export function ReviewSlider({ content }: ReviewSliderProps): JSX.Element {
  const [index, setIndex] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const slides = content.reviewsSection.highlights;
  const current = slides[index];

  useEffect(() => {
    if (prefersReducedMotion || slides.length <= 1) {
      return;
    }

    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => window.clearInterval(timer);
  }, [prefersReducedMotion, slides.length]);

  const goPrevious = () => setIndex((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setIndex((prev) => (prev + 1) % slides.length);

  const lastSynced = useMemo(
    () => formatDate(content.reviewsSection.summary.lastSynced, content.meta.locale),
    [content.meta.locale, content.reviewsSection.summary.lastSynced],
  );

  return (
    <section className="py-12 sm:py-16">
      <Container className="space-y-8">
        <SectionHeading title={content.reviewsSection.headline} description={content.reviewsSection.subheadline} />

        <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
          <Card className="bg-[linear-gradient(170deg,var(--color-surface)_0%,var(--color-primary-soft)_120%)]">
            <CardHeader>
              <CardTitle className="text-2xl">{content.reviewsSection.summary.ratingLabel}</CardTitle>
              <CardDescription>{content.reviewsSection.summary.reviewCountLabel}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-[color:var(--color-muted)]">
              <p>{content.reviewsSection.summary.sourceLabel}</p>
              <p>{lastSynced}</p>
              <p>{content.reviewsSection.sliderIntro}</p>
            </CardContent>
          </Card>

          <Card className="relative min-h-64 bg-white">
            <CardContent className="flex h-full flex-col justify-between gap-5 p-6 sm:p-7">
              <Quote className="h-7 w-7 text-[color:var(--color-primary)]" />
              <div className="space-y-3">
                <Stars rating={current.rating} />
                <p className="text-pretty text-base leading-relaxed text-[color:var(--color-text)]">{current.text}</p>
                <p className="text-sm text-[color:var(--color-muted)]">{`${current.reviewer} · ${formatDate(current.date, content.meta.locale)}`}</p>
              </div>
            </CardContent>
            <div className="absolute bottom-4 right-4 flex items-center gap-2">
              <Button size="icon" variant="outline" onClick={goPrevious} aria-label={content.reviewsSection.summary.ratingLabel}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="outline" onClick={goNext} aria-label={content.reviewsSection.summary.reviewCountLabel}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <a href={content.reviewsSection.reviewCtas.primary.href}>{content.reviewsSection.reviewCtas.primary.label}</a>
          </Button>
          <Button asChild variant="outline">
            <a href={content.reviewsSection.reviewCtas.secondary.href}>{content.reviewsSection.reviewCtas.secondary.label}</a>
          </Button>
        </div>
      </Container>
    </section>
  );
}
